import { useEffect, useState } from "react";

import { useLocalAudio } from "@/containers/Episode/LocalAudioContext";
import { AudioMention } from "@/types/Mention";
import getClosestMention from "@/utils/getClosestMention";
import AccessControl from "@components/AccessControl";
import InfoBox from "@components/InfoBox";
import Karaoke from "@components/Karaoke";
import PlayButtonWithControls from "@components/PlayButtonWithControls";

import { useEpisode } from "../../../context";
import Cover from "../../Cover";
import Favorite from "../../Favorite";
import Footer from "../../Footer";
import Header from "../../Header";
import MetaActions from "../../MetaActions";
import PaywallNotice from "../../PaywallNotice";
import ShareEpisode from "../../ShareEpisode";
import TextMentions from "../Text/TextMentions";
import Mention from "./Mention";
import Timeline from "./Timeline";

import styles from "./Audio.module.css";

const AudioEpisodeBody = () => {
  // Handle audio player, timeline etc here.
  const episode = useEpisode();
  const {
    audioMentions = [],
    textMentions = [],
    hideActions,
    can_mark_favorite,
    audio_url: episodeAudioUrl,
  } = episode;
  const [currentMention, setCurrentMention] = useState<AudioMention>(
    audioMentions[0]
  );
  const [manualTime, setManualTime] = useState<number | undefined>(undefined);

  useEffect(() => {
    setCurrentMention(audioMentions[0]);
  }, [audioMentions]);

  // Display the current mention start time or the current playing time:
  const {
    audio,
    playing,
    currentTime = 0,
    errored,
    loading,
    paused,
  } = useLocalAudio({
    currentTime: true,
    playing: true,
    errored: true,
    loading: true,
    paused: true,
  });

  // always pass a number to helpers
  const effectiveTime: number = audio
    ? (currentTime ?? 0)
    : (manualTime ?? currentTime ?? 0);

  useEffect(() => {
    const m = getClosestMention(audioMentions, effectiveTime);
    m && setCurrentMention(m as AudioMention);
  }, [audioMentions, effectiveTime]);

  if (audioMentions.length === 0) return null;

  const hasPreviousMention =
    audioMentions.length > 1 && audioMentions.indexOf(currentMention) - 1 >= 0;
  const hasNextMention =
    audioMentions.length > 1 &&
    audioMentions.indexOf(currentMention) + 1 < audioMentions.length;

  const seekPreviousMention = () => {
    if (hasPreviousMention) {
      const i = audioMentions.indexOf(currentMention) - 1;
      audio.seek(audioMentions[i].context_start_timing);
    }
  };

  const seekNextMention = () => {
    if (hasNextMention) {
      const i = audioMentions.indexOf(currentMention) + 1;
      audio.seek(audioMentions[i].context_start_timing);
    }
  };

  return (
    <div className={styles.root} data-testid="audio-episode">
      <div className={styles.top}>
        <div className={styles.inner}>
          <Header />
          <PaywallNotice />
          <MetaActions />

          {textMentions.length > 0 && (
            <TextMentions
              hideActions
              hideExternalLink
              hideMetadata
              visibleMentions={1}
            />
          )}

          {!episodeAudioUrl ? (
            <InfoBox text={L.MENTIONS_AUDIO_UNAVAILABLE} />
          ) : null}

          <Timeline
            visible={currentMention}
            onMarkerClick={(clicked) => {
              setCurrentMention(clicked);

              if (episodeAudioUrl && audio) {
                audio.seek(clicked.context_start_timing);
                setManualTime(undefined); // audio drives karaoke again
              } else {
                setManualTime(clicked.context_start_timing); // manual highlight
              }
            }}
          />

          <div className={styles.content}>
            {episodeAudioUrl ? (
              <PlayButtonWithControls
                error={errored}
                loading={loading}
                playing={playing}
                onClick={() => {
                  if (playing) return audio.pause();
                  if (paused) return audio.play();
                  audio.play(currentMention.context_start_timing);
                }}
                onClickPrevious={seekPreviousMention}
                onClickNext={seekNextMention}
                previousDisabled={!hasPreviousMention}
                nextDisabled={!hasNextMention}
              />
            ) : null}
            <div>
              <div className={styles.transcript}>
                <Karaoke manualCurrentTime={manualTime} />
              </div>
              {currentMention && <Mention mention={currentMention} />}
            </div>
          </div>
        </div>

        <div className={styles.cover}>
          <Cover episode={episode} />

          {!hideActions && can_mark_favorite && (
            <AccessControl>
              <Favorite className={styles.favorite} />
            </AccessControl>
          )}
          <ShareEpisode className={styles.share} />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AudioEpisodeBody;
